import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminConfirmPayment, adminRejectPayment, fetchAdminPayments, type AdminPayment } from "../../utils/api";
import { Icon, SectionHead } from "../common/ui";

/**
 * Recent online payments, newest first — who paid, for what, and what state
 * the payment is in. Read-only: refunds are made in PayPal, and access stays
 * the admin's call (a refund is reported, never auto-revoked).
 */
const STATUS_CLASS: Record<string, string> = {
  completed: "badge badge-success",
  created: "badge",
  failed: "badge",
  claimed: "badge badge-amber",
  refunded: "badge badge-violet",
  reversed: "badge badge-violet",
  cancelled: "badge",
  // Money taken but no access granted — the admin refunds or grants by hand.
  credit_conflict: "badge badge-amber",
  paid_cancelled: "badge badge-amber",
};

export default function PaymentsPanel({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<AdminPayment[] | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchAdminPayments(token).then(setRows).catch(() => setRows([]));
  }, [token]);

  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language?.startsWith("en") ? "en-GB" : "ru-RU", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  // Abandoned checkouts ("created" and never paid) are noise unless asked for;
  // a link payment the customer says they paid ("claimed") always shows.
  const shown = (rows ?? []).filter((r) => showAll || r.status !== "created");
  const paid = (rows ?? []).filter((r) => r.status === "completed" && r.environment === "live");
  const total = paid.filter((r) => r.currency === "USD").reduce((n, r) => n + Number(r.amount), 0);
  const totalRub = paid.filter((r) => r.currency === "RUB").reduce((n, r) => n + Number(r.amount), 0);
  const totalByn = paid.filter((r) => r.currency === "BYN").reduce((n, r) => n + Number(r.amount), 0);
  const waiting = (rows ?? []).filter((r) => r.provider === "bepaid_link" && r.status === "claimed").length;
  const [busyId, setBusyId] = useState<string | null>(null);
  const act = async (r: AdminPayment, kind: "confirm" | "reject") => {
    if (kind === "reject" && !window.confirm(t("payments.rejectConfirm"))) return;
    setBusyId(r.id);
    try {
      const updated = await (kind === "confirm" ? adminConfirmPayment : adminRejectPayment)(r.id, token);
      setRows((prev) => (prev ?? []).map((x) => (x.id === r.id ? { ...x, ...updated } : x)));
    } finally {
      setBusyId(null);
    }
  };
  const money = (r: AdminPayment) => r.currency === "RUB"
    ? `${Number(r.amount).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`
    : `$${Number(r.amount).toFixed(2)}`;

  return (
    <section className="glass glass-lg" style={{ padding: 20 }}>
      <SectionHead icon="wallet">{t("payments.title")}</SectionHead>
      <p className="t-xs muted-2" style={{ margin: "0 0 12px", lineHeight: 1.45 }}>
        {t("payments.hint")}
      </p>
      {waiting > 0 && (
        <p className="pay-waiting">{t("payments.waiting", { count: waiting })}</p>
      )}
      {rows === null ? (
        <div className="spinner spinner-amber" style={{ margin: "16px auto" }} />
      ) : shown.length === 0 ? (
        <p className="t-sm muted" style={{ margin: 0 }}>{t("payments.empty")}</p>
      ) : (
        <ul className="pay-list">
          {shown.slice(0, 30).map((r) => (
            <li key={r.id}>
              <span className="pay-list__main">
                <span className="t-sm" style={{ fontWeight: 700 }}>{r.buyer || r.payer_email || "—"}</span>
                <span className="t-xs muted-2">
                  {fmt(r.created_at)} · {r.plan_code || "—"}
                  {r.environment !== "live" && <span className="pay-list__env">sandbox</span>}
                  {r.provider === "bepaid_link" && <span className="pay-list__env is-bepaid">bePaid</span>}
                </span>
              </span>
              <span className="pay-list__amount">{money(r)}</span>
              <span className={STATUS_CLASS[r.status] ?? "badge"}>{t(`payments.status_${r.status}`, { defaultValue: r.status })}</span>
              {r.provider === "bepaid_link" && (r.status === "created" || r.status === "claimed") && (
                <span className="pay-list__actions">
                  <button className="btn btn-primary" disabled={busyId === r.id} onClick={() => act(r, "confirm")}>
                    {busyId === r.id ? <span className="spinner" /> : <><Icon name="check" size={15} /> {t("payments.confirm")}</>}
                  </button>
                  <button className="btn btn-ghost" disabled={busyId === r.id} onClick={() => act(r, "reject")}>{t("payments.reject")}</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="between" style={{ marginTop: 12, flexWrap: "wrap", gap: 8 }}>
        <span className="t-xs muted-2">
          {t("payments.liveTotal", { count: paid.length, total: total.toFixed(2) })}
          {totalRub > 0 && ` · ${totalRub.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`}
          {totalByn > 0 && ` · ${totalByn.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} BYN`}
        </span>
        <button className="btn btn-ghost" onClick={() => setShowAll((v) => !v)} style={{ minHeight: 32, paddingInline: 10 }}>
          <Icon name={showAll ? "eye" : "history"} size={15} /> {showAll ? t("payments.hideUnpaid") : t("payments.showUnpaid")}
        </button>
      </div>
    </section>
  );
}
